import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBlogPosts } from '../../../hooks/useBlogPosts';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { ArrowLeft, Edit, Calendar, User, Tag } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useTheme } from '../../../components/ui/theme-provider';

const BlogPostPreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchPost, loading, error } = useBlogPosts();
  const { theme } = useTheme();
  const [post, setPost] = useState(null);

  useEffect(() => {
    const loadPost = async () => {
      if (id) {
        try {
          const fetchedPost = await fetchPost(id);
          setPost(fetchedPost);
        } catch (error) {
          console.error('Error loading post for preview:', error);
        }
      }
    };

    loadPost();
  }, [id, fetchPost]);

  if (loading) {
    return (
      <div className="container mx-auto p-4 max-w-4xl">
        <div className="animate-pulse">
          <div className="h-8 w-32 bg-muted rounded mb-6"></div>
          <div className="h-12 w-3/4 bg-muted rounded mb-4"></div>
          <div className="h-4 w-full bg-muted rounded mb-2"></div>
          <div className="h-4 w-2/3 bg-muted rounded mb-8"></div>
          <div className="space-y-4">
            <div className="h-4 w-full bg-muted rounded"></div>
            <div className="h-4 w-full bg-muted rounded"></div>
            <div className="h-4 w-3/4 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="container mx-auto p-4 max-w-4xl">
        <Card>
          <CardContent className="p-6 text-center">
            <h2 className="text-xl font-bold mb-2 text-destructive">
              Error Loading Post
            </h2>
            <p className="text-muted-foreground mb-4">
              {error || 'The requested blog post could not be found.'}
            </p>
            <Button onClick={() => navigate('/admin/blog')} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Blog Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 max-w-4xl">
      {/* Header with navigation */}
      <div className="flex items-center justify-between mb-6">
        <Button
          onClick={() => navigate('/admin/blog')}
          variant="ghost"
          className="text-muted-foreground hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>
        
        <Button
          onClick={() => navigate(`/admin/blog/posts/edit/${post.id}`)}
          variant="outline"
        >
          <Edit className="h-4 w-4 mr-2" />
          Edit Post
        </Button>
      </div>

      {/* Preview Badge */}
      <div className="mb-4">
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
          Preview Mode
        </span>
      </div>

      {/* Blog Post Content */}
      <article className={cn(
        "prose prose-lg max-w-none",
        theme === 'dark' ? 'prose-invert' : ''
      )}>
        {/* Title */}
        <header className="mb-8 not-prose">
          <h1 className={cn(
            "text-4xl font-bold mb-4 leading-tight",
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          )}>
            {post.title}
          </h1>

          {/* Post Meta */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
            <div className="flex items-center gap-1">
              <User className="h-4 w-4" />
              <span>{post.author?.name || post.author?.email}</span>
            </div>
            
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>
                {post.publishedAt
                  ? new Date(post.publishedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })
                  : new Date(post.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })
                }
              </span>
            </div>

            <div className="flex items-center gap-1">
              <span className={cn(
                "px-2 py-1 rounded-full text-xs font-medium",
                post.status === 'published' 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : post.status === 'draft'
                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                  : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
              )}>
                {post.status}
              </span>
            </div>
          </div>

          {/* Categories & Tags */}
          {(post.categories?.length > 0 || post.tags?.length > 0) && (
            <div className="flex flex-wrap gap-2 mb-6">
              {post.categories?.map((category, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary/10 text-primary"
                >
                  <Tag className="h-3 w-3 mr-1" />
                  {category}
                </span>
              ))}
              {post.tags?.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-secondary text-secondary-foreground"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Excerpt */}
          {post.excerpt && (
            <p className={cn(
              "text-lg leading-relaxed italic",
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            )}>
              {post.excerpt}
            </p>
          )}
        </header>

        {/* Featured Image */}
        {post.featuredImage && (
          <div className="mb-8 not-prose">
            <img
              src={post.featuredImage}
              alt={post.title}
              className="w-full h-64 object-cover rounded-lg shadow-sm"
            />
          </div>
        )}

        {/* Post Content */}
        <div 
          className={cn(
            "prose prose-lg max-w-none",
            theme === 'dark' ? 'prose-invert' : '',
            "prose-headings:scroll-mt-20",
            "prose-a:text-primary prose-a:no-underline hover:prose-a:underline",
            "prose-blockquote:border-l-primary prose-blockquote:bg-muted/50",
            "prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded",
            "prose-pre:bg-muted prose-pre:border prose-pre:border-border"
          )}
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      </article>

      {/* Footer Actions */}
      <div className="mt-12 pt-8 border-t flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          <p>
            Created: {new Date(post.createdAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit'
            })}
          </p>
          {post.updatedAt && post.updatedAt !== post.createdAt && (
            <p>
              Updated: {new Date(post.updatedAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
              })}
            </p>
          )}
        </div>

        <div className="flex gap-2">
          <Button
            onClick={() => navigate('/admin/blog')}
            variant="outline"
          >
            Back to Dashboard
          </Button>
          <Button
            onClick={() => navigate(`/admin/blog/posts/edit/${post.id}`)}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit Post
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BlogPostPreview;
